import { Spinner, makeStyles, tokens, Badge } from '@fluentui/react-components';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router';
import { fetchKpis, fetchFinanceSummary } from '../api/dashboard.api';
import { fetchLotes } from '../api/lotes.api';
import { fetchRecentActivities } from '../api/lotes.api';
import { fetchExportKpis } from '../api/export.api';
import { fetchModules, type ModuleStatus } from '../api/modules.api';
import { fetchDroneFlights } from '../api/drone-flights.api';
import { KpiCard } from '../components/shared/KpiCard';
import { ActivityFeed } from '../components/shared/ActivityFeed';
import { ParcelStatusGrid, type ParcelStatusCell } from '../components/shared/ParcelStatusGrid';
import { DroneVideoCard } from '../components/shared/DroneVideoCard';
import { useRanchoStore } from '../store/rancho.store';
import { useProjectStore } from '../store/project.store';
import { useT } from '../i18n';
import { useLocaleSettings } from '../hooks/useLocaleSettings';
import { PageHeader } from '../components/shared/PageHeader';

/**
 * F7 — Resumen ejecutivo para el rol INVESTOR, pantalla 1 ("当日农场概况" /
 * "Resumen del día") del PDF del cliente.
 *
 * Fase B (2026-08-12): agrega el bloque de video-dron y el grid de módulos
 * reales coloreado por status (F1/F6, ya completos — el comentario anterior
 * de este archivo decía "F1/F2 aún sin construir", desactualizado desde
 * hace semanas). El grid de módulos solo se muestra si hay un proyecto
 * activo con módulos F1; si no, se mantiene el grid de Lotes como estaba
 * (tenants que todavía no adoptan Módulos/Proyectos).
 */

const useStyles = makeStyles({
  root:    { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, padding: tokens.spacingVerticalL },
  kpiRow:  { display: 'flex', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
  section: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  sectionTitle: { fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase400 },
  loteGrid: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalM },
  loteCard: {
    minWidth: '160px', padding: '14px 16px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  loteName: { fontWeight: tokens.fontWeightSemibold },
  loteCrop: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
});

/** Mismo criterio semáforo que `InvestorModulesPage` (STATUS_BORDER), pero en
 * relleno sólido — `ParcelStatusGrid` pinta el texto en blanco sobre `color`. */
const STATUS_FILL: Record<ModuleStatus, string> = {
  PREPARACION: '#8a8886',
  NORMAL:      '#107c10',
  ATENCION:    '#ca5010',
  CRITICO:     '#d83b01',
  COSECHANDO:  '#0078d4',
};

export function InvestorOverviewPage() {
  const s = useStyles();
  const t = useT();
  const navigate = useNavigate();
  const { tenantId } = useParams<{ tenantId: string }>();
  const { formatCurrency } = useLocaleSettings();
  const activeProjectId = useProjectStore(st => st.activeProjectId);

  const activeRanchoId = useRanchoStore(st => st.activeRanchoId);

  const { data: kpis, isLoading: kpisLoading } = useQuery({ queryKey: ['investor-kpis', activeRanchoId], queryFn: () => fetchKpis(activeRanchoId) });
  const { data: finance, isLoading: financeLoading } = useQuery({ queryKey: ['investor-finance', activeRanchoId], queryFn: () => fetchFinanceSummary(activeRanchoId) });
  const { data: exportKpis } = useQuery({ queryKey: ['investor-export-kpis', activeRanchoId], queryFn: () => fetchExportKpis(activeRanchoId) });
  const { data: flights = [] } = useQuery({ queryKey: ['investor-overview-drone-flights', activeRanchoId], queryFn: () => fetchDroneFlights({ ranchoId: activeRanchoId }) });
  const { data: allModules = [], isLoading: modulesLoading } = useQuery({
    queryKey: ['investor-overview-modules', activeProjectId, activeRanchoId],
    queryFn: () => fetchModules({ projectId: activeProjectId!, ranchoId: activeRanchoId }),
    enabled: !!activeProjectId,
  });
  const { data: lotes = [], isLoading: lotesLoading } = useQuery({ queryKey: ['investor-lotes', activeRanchoId], queryFn: () => fetchLotes(activeRanchoId) });
  const { data: activities = [], isLoading: actsLoading } = useQuery({
    queryKey: ['investor-activities', activeRanchoId],
    queryFn: () => fetchRecentActivities(10, activeRanchoId),
  });
  // Los módulos se piden por proyecto; con un rancho activo se quedan los de sus lotes.
  const modules = activeRanchoId ? allModules.filter(m => lotes.some(l => l.loteId === m.loteId)) : allModules;

  return (
    <div className={s.root}>
      <PageHeader title={t.investor.title} subtitle={t.investor.subtitle} />

      <DroneVideoCard
        flights={flights}
        title={t.investor.droneVideo}
        loadingLabel={t.investorModules.loadingVideo}
        errorLabel={t.investorModules.videoLoadError}
      />

      {/* KPIs generales */}
      {kpisLoading && <Spinner size="small" label={t.investor.loading} />}
      {kpis && (
        <div className={s.kpiRow}>
          <KpiCard label={t.investor.kpiLotes} value={kpis.loteCount} />
          <KpiCard label={t.investor.kpiActivities} value={kpis.activityCount} sub={t.investor.kpiActivitiesSub} />
          <KpiCard label={t.investor.kpiCostToday} value={formatCurrency(kpis.costToday ?? 0)} />
          <KpiCard label={t.investor.kpiPendingTasks} value={kpis.pendingTasks} />
          <KpiCard label={t.investor.kpiOverdueTasks} value={kpis.overdueTasks} accent={kpis.overdueTasks > 0} />
        </div>
      )}

      {/* Inversión / costos */}
      <div className={s.section}>
        <span className={s.sectionTitle}>{t.investor.financeTitle}</span>
        {financeLoading && <Spinner size="small" />}
        {finance && (
          <div className={s.kpiRow}>
            <KpiCard label={t.investor.kpiTotalCost} value={formatCurrency(finance.totalCost ?? 0)} />
          </div>
        )}
      </div>

      {/* Logística / exportación */}
      {exportKpis && (
        <div className={s.section}>
          <span className={s.sectionTitle}>{t.investor.logisticsTitle}</span>
          <div className={s.kpiRow}>
            <KpiCard label={t.investor.kpiShipments} value={exportKpis.totalShipments} />
            <KpiCard label={t.investor.kpiInTransit} value={exportKpis.activeShipments} />
            <KpiCard label={t.investor.kpiHeld} value={exportKpis.onHoldShipments} accent={exportKpis.onHoldShipments > 0} />
            <KpiCard label={t.investor.kpiDelivered} value={exportKpis.deliveredShipments} />
            {exportKpis.tempAnomalies > 0 && (
              <KpiCard label={t.investor.kpiTempAnomalies} value={exportKpis.tempAnomalies} accent />
            )}
          </div>
        </div>
      )}

      {/* Grid de módulos por status (F1) — reemplaza el fallback de Lotes cuando
          el proyecto activo ya tiene módulos reales. */}
      {activeProjectId && modules.length > 0 ? (
        <div className={s.section}>
          <span className={s.sectionTitle}>{t.investor.modulesTitle}</span>
          {modulesLoading && <Spinner size="small" />}
          <ParcelStatusGrid
            columns={6}
            cellHeight={56}
            onCellClick={(cell: ParcelStatusCell) => navigate(`/${tenantId}/fincas/modulo/${cell.key}`)}
            cells={modules.map(m => ({ key: m.moduleId, label: m.moduleCode, color: STATUS_FILL[m.status] }))}
            legend={(Object.keys(STATUS_FILL) as ModuleStatus[]).map(st => ({
              label: t.investorModules[`status${st}` as keyof typeof t.investorModules] as string,
              color: STATUS_FILL[st],
            }))}
          />
        </div>
      ) : (
        <div className={s.section}>
          <span className={s.sectionTitle}>{t.investor.lotesTitle}</span>
          {lotesLoading && <Spinner size="small" />}
          <div className={s.loteGrid}>
            {lotes.map(l => (
              <div key={l.loteId} className={s.loteCard}>
                <div className={s.loteName}>{l.name}</div>
                <div className={s.loteCrop}>{l.cultivo || t.investor.noCrop}</div>
                {l.hectareas != null && <Badge appearance="tint" size="small">{l.hectareas} ha</Badge>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bitácora reciente */}
      <div className={s.section}>
        <span className={s.sectionTitle}>{t.investor.activityTitle}</span>
        <ActivityFeed activities={activities} isLoading={actsLoading} />
      </div>
    </div>
  );
}
